/* eslint-disable @typescript-eslint/consistent-type-imports */
import {
  Typography,
  useTheme,
  Box,
  Chip,
  InputLabel,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import { type GridColumns, type GridValidRowModel } from '@mui/x-data-grid';
import CustomDataGrid from 'components/customDataGrid/CustomDataGrid';
import { Fragment, useState, useEffect } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { useSelector, useDispatch } from 'react-redux';
import { type RootState } from 'store';

import {
  exportComplaints,
  getComplaintByUserId,
  updateComplaintStatus,
} from 'service/ComplaintService';
import {
  createComplatintForSupport,
  getAllSupportEmployeeList,
} from 'service/OperationalTeamService';
import {
  ComplaintAttributesType,
  SupportTeamEmployeeType,
} from 'types/services';
import { setSnackBarFailed, setSnackBarSuccess } from 'store/snackBar';
import { strings } from 'i18n/Strings';
import { colorTheme } from 'theme';
import AddComplaints from 'pages/Complaints/AddComplaints';
import { DialogBox } from 'components';
import { commonConstantTexts } from 'constants/constantText';
import { dateFormat } from 'utils/GeneralUtil';

type GridRowData = Record<string, unknown>;

const ComplaintsRaised = (): JSX.Element => {
  const currentTheme = useTheme();
  const navigate = useNavigate();
  const colors = colorTheme(currentTheme.palette.mode);
  const { empId } = useSelector((state: RootState) => state.onboarding);
  const [complaints, setComplaints] = useState<ComplaintAttributesType[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [supportEmployeeList, setSupportEmployeeList] = useState<
    SupportTeamEmployeeType[]
  >([]);
  const { roles } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const [pageCount, setPageCount] = useState(null);
  const pageInfo = useSelector((state) => state?.pagination?.complaints);
  const { id } = useParams();

  const handleEvent = (id: number): void => {
    navigate(`/customer-complaints/${id}`);
  };

  const getComplaintDetails = (): void => {
    if (id) {
      getComplaintByUserId(id, Number(pageInfo?.page) + 1, pageInfo?.pageSize)
        .then((res) => {
          if (typeof res !== 'undefined' && 'basicInfo' in res) {
            const { basicInfo } = res as {
              basicInfo: ComplaintAttributesType[];
            };
            if (basicInfo) {
              setComplaints(basicInfo?.rows);
              setPageCount(basicInfo?.count);
            }
          }
        })
        .catch((err: Error) => {
          throw err;
        });
    }
  };
  const getSupportEmployeeList = (): void => {
    getAllSupportEmployeeList()
      .then((res) => {
        if (typeof res !== 'undefined' && 'data' in res) {
          const { data } = res as {
            data: SupportTeamEmployeeType[];
          };
          if (data) {
            setSupportEmployeeList(data);
          }
        }
      })
      .catch((err) => err);
  };

  const handleSelectChange = (event, row): void => {
    const { value } = event.target;
    const data = {
      supportEmployeeId: value,
      createdBy: empId,
      updatedBy: empId,
      complaintId: row.complaintId,
    };
    const newComplaints = complaints.map((complaint) => {
      if (complaint.complaintId === row.complaintId) {
        return {
          ...complaint,
          agentId: Number(value),
        };
      } else {
        return complaint;
      }
    });

    setComplaints(newComplaints);
    void createComplatintForSupport(data);
  };

  const handleExport = (): void => {
    exportComplaints(complaints)
      .then((res) => {
        dispatch(
          setSnackBarSuccess({
            snackBarMessage: strings.displayText.exportComplaintSuccess,
          }),
        );
      })
      .catch((err) => {
        dispatch(
          setSnackBarFailed({
            snackBarMessage: err?.response?.data?.message,
          }),
        );
      });
  };

  useEffect(() => {
    getComplaintDetails();
    getSupportEmployeeList();
  }, [pageInfo?.page, pageInfo?.pageSize]);

  const handleUpdateStatus = async (
    complaintId: number,
    status: string,
  ): Promise<void> => {
    if (complaintId) {
      const data = {
        status,
        userId,
        createdBy: empId,
        createdByType: 'employee',
      };

      await updateComplaintStatus(complaintId, data);
    }

    getComplaintDetails();
  };

  const handleCreateDialogClose = (): void => {
    setIsModalOpen(false);
  };

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'createdAt',
      flex: 1,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.dateColumn}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {dateFormat(row?.createdAt)}
        </Typography>
      ),
    },
    {
      field: 'complaintId',
      flex: 1,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.complaintId}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.complaintId}
        </Typography>
      ),
    },
    {
      field: 'firstName',
      flex: 1,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.nameColumn}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.firstName} {row?.lastName}
        </Typography>
      ),
    },

    {
      field: 'complaintTitle',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.titleColumn}
        </Typography>
      ),
      flex: 1,
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.complaintTitle}
        </Typography>
      ),
    },
    {
      field: 'description',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.descriptionColumn}
        </Typography>
      ),
      flex: 1,
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.description}
        </Typography>
      ),
    },
    {
      field: 'assign',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.assignColumn}
        </Typography>
      ),
      flex: 1,
      renderCell: ({ row }) => {
        // <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
        //   {'-'}
        // </Typography>
        const activeValue = supportEmployeeList.find(
          (employee) => Number(employee.supportEmployeeId) === row.agentId,
        );
        return (
          <FormControl fullWidth>
            <InputLabel id="admin-detail-select-label">
              {strings.displayText.assignColumn}
            </InputLabel>

            <Select
              labelId="admin-detail-select-label"
              margin="normal"
              label={strings.displayText.assignColumn}
              value={activeValue?.supportEmployeeId}
              onChange={(event: unknown) => {
                handleSelectChange(event, row);
              }}
            >
              {supportEmployeeList.map((element, i) => (
                <MenuItem key={i} value={element.supportEmployeeId}>
                  {element.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      },
    },
    {
      field: 'status',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.statusColumn}
        </Typography>
      ),
      flex: 1,
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.status}
        </Typography>
      ),
    },
    {
      field: 'actions',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.actionsColumn}
        </Typography>
      ),
      flex: 1,
      renderCell: ({ row }) => (
        <Box>
          {row?.status === 'New' ? (
            '-'
          ) : (
            <>
              {row?.status === 'Close' ? (
                <Chip
                  label={strings.displayText.reOpenLable}
                  variant="outlined"
                  color="error"
                  onClick={(event: { stopPropagation: () => void }) => {
                    event.stopPropagation();
                    const reopen = 'Re-open';
                    void handleUpdateStatus(row?.complaintId, reopen);
                  }}
                />
              ) : (
                <Chip
                  label={strings.displayText.closeLable}
                  variant="outlined"
                  color="success"
                  onClick={(event: { stopPropagation: () => void }) => {
                    event.stopPropagation();
                    const close = 'Close';
                    void handleUpdateStatus(row?.complaintId, close);
                  }}
                />
              )}
            </>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Fragment>
      <CustomDataGrid
        columns={columns}
        rows={complaints && (complaints as GridRowData[])}
        onRowClick={(row) => {
          const id: string | number = row?.row?.complaintId;
          handleEvent(id);
        }}
        onAddButtonClick={() => {
          setIsModalOpen(true);
        }}
        getRowId={(row: GridRowData) =>
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions, @typescript-eslint/no-base-to-string
          `${String(row?.complaintId)}${row.email ? row.email : ''}`
        }
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0
            ? commonConstantTexts.evenText
            : commonConstantTexts.oddText
        }
        isShowExport={roles?.includes('ceo')}
        onExportClick={handleExport}
        currentPage={commonConstantTexts.complaintsPage}
        pageCount={pageCount}
      />
      <DialogBox
        open={isModalOpen}
        onClose={handleCreateDialogClose}
        title={strings.displayText.createComplaintText}
      >
        <AddComplaints
          onClose={handleCreateDialogClose}
          getComplaintDetails={getComplaintDetails}
          supportEmpList={supportEmployeeList}
        />
      </DialogBox>
    </Fragment>
  );
};

export default ComplaintsRaised;
